import React, { useEffect } from 'react';
import { Box, Card, CardContent, Grid, Radio, RadioGroup, Typography, CircularProgress, Alert, Chip } from '@mui/joy';
import { useCmsThemes } from '../../../hooks/useCmsThemes';

interface ThemeStepProps {
  value: string;
  onChange: (themeId: string) => void;
}

export const ThemeStep: React.FC<ThemeStepProps> = ({ value, onChange }) => {
  const { themes, organization, loading, error, getThemePreviewStyle } = useCmsThemes();

  // Set initial value when themes are loaded
  useEffect(() => {
    if (themes.length > 0 && !value) {
      // Default to the first theme if no value is set
      onChange(themes[0].key);
    }
  }, [themes, value, onChange]);

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" py={4}>
        <CircularProgress size="lg" />
        <Typography level="body-md" mt={2}>Loading available themes...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert color="danger" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (themes.length === 0) {
    return (
      <Alert color="warning" sx={{ mb: 2 }}>
        No themes available. Please contact support.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography level="h4" mb={1}>
        Pick Your Website Style
      </Typography>
      
      {organization?.has_branding && (
        <Alert color="success" sx={{ mb: 3 }}>
          <Typography level="body-sm">
            Themes will automatically use your brand colors and fonts:
            {organization.brand_colors.primary && (
              <Chip
                size="sm"
                sx={{ 
                  ml: 1,
                  backgroundColor: organization.brand_colors.primary,
                  color: '#fff'
                }}
              >
                Primary
              </Chip>
            )}
            {organization.brand_colors.secondary && (
              <Chip
                size="sm"
                sx={{ 
                  ml: 1,
                  backgroundColor: organization.brand_colors.secondary,
                  color: '#fff'
                }}
              >
                Secondary
              </Chip>
            )}
            {organization.brand_fonts.heading && (
              <Typography level="body-xs" sx={{ ml: 2, display: 'inline' }}>
                Font: {organization.brand_fonts.heading}
              </Typography>
            )}
          </Typography>
        </Alert>
      )}

      <RadioGroup value={value} onChange={(e) => onChange((e.target as HTMLInputElement).value)}>
        <Grid container spacing={3}>
          {themes.map((theme) => {
            const previewStyle = getThemePreviewStyle(theme) as any;
            const isSelected = value === theme.key;
            
            return (
              <Grid xs={12} sm={6} md={4} key={theme.key}>
                <Card
                  onClick={() => onChange(theme.key)}
                  variant={isSelected ? 'solid' : 'outlined'}
                  sx={{ 
                    cursor: 'pointer',
                    height: '100%',
                    transition: 'all 0.2s',
                    transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      boxShadow: 'lg'
                    }
                  }}
                >
                  <Box
                    sx={{
                      height: 140,
                      background: `linear-gradient(135deg, ${previewStyle['--theme-primary']} 0%, ${previewStyle['--theme-secondary']} 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    {/* Theme preview elements */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 10,
                        left: 10,
                        width: 30,
                        height: 4,
                        backgroundColor: previewStyle['--theme-accent'],
                        borderRadius: 2
                      }}
                    />
                    <Typography
                      level="h3"
                      sx={{
                        color: '#fff',
                        fontFamily: previewStyle['--theme-font-heading'],
                        textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        fontSize: '1.2rem'
                      }}
                    >
                      {theme.name}
                    </Typography>
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 10,
                        right: 10,
                        display: 'flex',
                        gap: 0.5
                      }}
                    >
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          backgroundColor: previewStyle['--theme-primary'],
                          border: '2px solid #fff'
                        }}
                      />
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          backgroundColor: previewStyle['--theme-secondary'],
                          border: '2px solid #fff'
                        }}
                      />
                      {previewStyle['--theme-accent'] && (
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            backgroundColor: previewStyle['--theme-accent'],
                            border: '2px solid #fff'
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                  
                  <CardContent>
                    <Typography level="title-lg" mb={0.5}>
                      {theme.name}
                    </Typography>
                    <Typography level="body-sm" mb={1}>
                      {theme.preview.description}
                    </Typography>
                    {theme.preview.has_branding && (
                      <Chip size="sm" color="success" variant="soft">
                        Branded
                      </Chip>
                    )}
                    <Radio 
                      value={theme.key} 
                      sx={{ 
                        mt: 1,
                        ml: 'auto',
                        mr: 'auto',
                        display: 'block'
                      }} 
                    />
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </RadioGroup>
    </Box>
  );
};

export default ThemeStep;
